import {
  Activity,
  AlertTriangle,
  Calendar,
  CheckCircle2,
  Clock,
  Filter,
  Loader2,
  RefreshCcw,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { readPublicError } from '@/api/error';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TimestampDisplay } from '@/components/ui/timestamp-display';

interface Measurement {
  id: number;
  monitor_id: number;
  response_time_ms: number | null;
  http_status_code: number | null;
  response_body: string | null;
  error_message: string | null;
  success: boolean;
  checked_at: string;
  created_at: string;
}

interface Monitor {
  id: number;
  url: string;
  name: string;
  enabled: boolean;
}

const TIME_RANGES = {
  all: { label: 'Latest 50 checks', hours: 0 },
  '1h': { label: 'Last hour', hours: 1 },
  '6h': { label: 'Last 6 hours', hours: 6 },
  '24h': { label: 'Last 24 hours', hours: 24 },
  '7d': { label: 'Last 7 days', hours: 168 },
  '30d': { label: 'Last 30 days', hours: 720 },
  custom: { label: 'Custom range', hours: 0 },
};

export function MonitorResultsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [monitor, setMonitor] = useState<Monitor | null>(null);
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [monitorError, setMonitorError] = useState<string | null>(null);
  const [measurementError, setMeasurementError] = useState<string | null>(null);
  const [hasLoadedMeasurements, setHasLoadedMeasurements] = useState(false);

  const fetchMonitor = useCallback(async () => {
    try {
      const response = await fetch(`/api/monitors/${id}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setMonitor(data);
        setMonitorError(null);
      } else if (response.status === 404) {
        navigate('/dashboard/monitors');
      } else {
        setMonitorError(await readPublicError(response, 'Failed to load monitor'));
      }
    } catch {
      setMonitorError('Failed to load monitor');
    }
  }, [id, navigate]);

  const fetchMeasurements = useCallback(async () => {
    setLoading(true);
    setMeasurementError(null);
    try {
      let url = `/api/monitors/${id}/measurements?limit=50`;

      if (timeRange !== 'custom') {
        const hours = TIME_RANGES[timeRange as keyof typeof TIME_RANGES].hours;
        if (hours > 0) {
          const startDate = new Date();
          startDate.setHours(startDate.getHours() - hours);
          url += `&startDate=${startDate.toISOString()}`;
        }
      } else if (customStartDate || customEndDate) {
        if (customStartDate) {
          url += `&startDate=${new Date(customStartDate).toISOString()}`;
        }
        if (customEndDate) {
          url += `&endDate=${new Date(customEndDate).toISOString()}`;
        }
      }

      const response = await fetch(url, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setMeasurements(data);
        setHasLoadedMeasurements(true);
      } else {
        setMeasurementError(await readPublicError(response, 'Failed to load monitor results'));
      }
    } catch {
      setMeasurementError('Failed to load monitor results');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [customEndDate, customStartDate, id, timeRange]);

  useEffect(() => {
    // Validate ID is a valid number
    const monitorId = id ? Number.parseInt(id, 10) : Number.NaN;
    if (Number.isNaN(monitorId)) {
      navigate('/dashboard/monitors');
      return;
    }

    fetchMonitor();
  }, [fetchMonitor, id, navigate]);

  useEffect(() => {
    if (id && timeRange !== 'custom') {
      fetchMeasurements();
    }
  }, [fetchMeasurements, id, timeRange]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchMonitor();
    fetchMeasurements();
  };

  const handleCustomDateFilter = () => {
    if (timeRange === 'custom') {
      fetchMeasurements();
    }
  };

  const getStatusColor = (success: boolean, statusCode: number | null) => {
    if (!success) return 'text-red-500';
    if (statusCode && statusCode >= 200 && statusCode < 300) return 'text-green-500';
    if (statusCode && statusCode >= 300 && statusCode < 400) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getStatusIcon = (success: boolean) => {
    if (success) {
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    }
    return <XCircle className="h-4 w-4 text-red-500" />;
  };

  const getResponseTimeColor = (responseTime: number | null) => {
    if (!responseTime) return 'text-gray-400';
    if (responseTime < 200) return 'text-green-600';
    if (responseTime < 500) return 'text-yellow-600';
    if (responseTime < 1000) return 'text-orange-600';
    return 'text-red-600';
  };

  const calculateStats = () => {
    const successCount = measurements.filter((m) => m.success).length;
    const avgResponseTime =
      measurements
        .filter((m) => m.response_time_ms !== null)
        .reduce((acc, m) => acc + (m.response_time_ms || 0), 0) /
      measurements.filter((m) => m.response_time_ms !== null).length;

    return {
      total: measurements.length,
      successful: successCount,
      failed: measurements.length - successCount,
      uptime: measurements.length > 0 ? (successCount / measurements.length) * 100 : 0,
      avgResponseTime: Number.isNaN(avgResponseTime) ? 0 : avgResponseTime,
    };
  };

  const stats = calculateStats();

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900">
              {monitor?.name || 'Monitor'} Results
            </h1>
            <p className="text-sm text-gray-500 mt-1 break-words">{monitor?.url}</p>
          </div>
          <div className="flex flex-wrap gap-2 sm:shrink-0">
            <Button
              onClick={() => navigate(`/dashboard/monitors/edit?id=${id}`)}
              variant="outline"
              size="sm"
            >
              Edit Monitor
            </Button>
            <Button onClick={() => navigate('/dashboard/monitors')} variant="outline" size="sm">
              Back to Monitors
            </Button>
          </div>
        </div>
        {monitorError ? (
          <Alert className="mt-4" variant="destructive">
            <AlertDescription>{monitorError}</AlertDescription>
          </Alert>
        ) : null}
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-5 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Checks</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{hasLoadedMeasurements ? stats.total : '—'}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Successful</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {hasLoadedMeasurements ? stats.successful : '—'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Failed</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {hasLoadedMeasurements ? stats.failed : '—'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Uptime</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {hasLoadedMeasurements ? `${stats.uptime.toFixed(2)}%` : '—'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Response</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {hasLoadedMeasurements ? `${stats.avgResponseTime.toFixed(0)}ms` : '—'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Probe Results</CardTitle>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <Filter className="h-4 w-4" />
                Filters
              </Button>
              <Button
                onClick={handleRefresh}
                variant="outline"
                size="sm"
                disabled={refreshing}
                className="gap-2"
              >
                {refreshing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCcw className="h-4 w-4" />
                )}
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>

        {showFilters && (
          <CardContent className="border-b">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
              <div className="flex-1">
                <Label htmlFor="time-range">Time Range</Label>
                <Select value={timeRange} onValueChange={setTimeRange}>
                  <SelectTrigger id="time-range">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TIME_RANGES).map(([key, value]) => (
                      <SelectItem key={key} value={key}>
                        {value.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {timeRange === 'custom' && (
                <>
                  <div className="flex-1">
                    <Label htmlFor="start-date">Start Date</Label>
                    <Input
                      id="start-date"
                      type="datetime-local"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                    />
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="end-date">End Date</Label>
                    <Input
                      id="end-date"
                      type="datetime-local"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                    />
                  </div>
                  <Button onClick={handleCustomDateFilter} size="sm">
                    Apply
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        )}

        {/* Results Table */}
        <CardContent className="p-0">
          {measurementError ? (
            <Alert className="m-4" variant="destructive">
              <AlertDescription>{measurementError}</AlertDescription>
            </Alert>
          ) : null}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : hasLoadedMeasurements && measurements.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-3" />
              <p>No probe results found for the selected time range.</p>
            </div>
          ) : hasLoadedMeasurements ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Status</TableHead>
                    <TableHead>Checked At</TableHead>
                    <TableHead>Response Time</TableHead>
                    <TableHead>HTTP Status</TableHead>
                    <TableHead>Error Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {measurements.map((measurement) => (
                    <TableRow key={measurement.id} className="hover:bg-gray-50">
                      <TableCell className="text-center">
                        {getStatusIcon(measurement.success)}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        <TimestampDisplay date={measurement.checked_at} />
                      </TableCell>
                      <TableCell>
                        {measurement.response_time_ms ? (
                          <span
                            className={`font-semibold ${getResponseTimeColor(
                              measurement.response_time_ms,
                            )}`}
                          >
                            {measurement.response_time_ms}ms
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {measurement.http_status_code ? (
                          <span
                            className={`font-semibold ${getStatusColor(
                              measurement.success,
                              measurement.http_status_code,
                            )}`}
                          >
                            {measurement.http_status_code}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {measurement.error_message ? (
                          <span className="text-red-600 text-sm">{measurement.error_message}</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
